import { useCallback, useEffect, useState } from "react";
import { api, ApiClientError } from "../../lib/apiClient.js";
import type { MaterialDto, WorkCenterDto } from "@grizzly/shared";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";
import { Plus, Trash2 } from "lucide-react";

interface RouteStep {
  workCenterId: string;
  description: string;
  setupMinutes: string;
  runMinutesPerUnit: string;
}

interface BomLine {
  materialId: string;
  quantityPer: string;
  scrapFactor: string;
}

export function ProductRevisionDialog({
  productId,
  open,
  onOpenChange,
  onCreated,
}: {
  productId: string;
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState("");
  const [routing, setRouting] = useState<RouteStep[]>([]);
  const [bom, setBom] = useState<BomLine[]>([]);
  const [workCenters, setWorkCenters] = useState<WorkCenterDto[]>([]);
  const [materials, setMaterials] = useState<MaterialDto[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .listWorkCenters()
      .then((r) => setWorkCenters(r.items))
      .catch(() => undefined);
    api
      .listMaterials()
      .then((r) => setMaterials(r.items))
      .catch(() => undefined);
  }, [open]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.createProductRevision(productId, {
        description,
        routing: routing
          .filter((s) => s.description.trim())
          .map((s, idx) => ({
            sequence: idx,
            workCenterId: s.workCenterId || "",
            description: s.description,
            setupMinutes: Number(s.setupMinutes || "0"),
            runMinutesPerUnit: Number(s.runMinutesPerUnit || "0"),
          })),
        bom: bom
          .filter((b) => b.materialId)
          .map((b) => ({
            materialId: b.materialId,
            quantityPer: b.quantityPer || "1",
            scrapFactor: b.scrapFactor || "0",
          })),
      });
      push(t("catalog.productRevision.releasedToast"), description || t("common.saving"));
      setDescription("");
      setRouting([]);
      setBom([]);
      onOpenChange(false);
      onCreated();
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [bom, description, onCreated, onOpenChange, productId, push, routing, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("catalog.productRevision.title")}
      description={t("catalog.productRevision.description")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? t("common.saving") : t("catalog.productRevision.releaseRevision")}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pe-1">
        <TextInput
          label={t("common.fields.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              {t("catalog.productRevision.routing")}
            </h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setRouting((r) => [
                  ...r,
                  {
                    workCenterId: "",
                    description: "",
                    setupMinutes: "0",
                    runMinutesPerUnit: "0",
                  },
                ])
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("catalog.productRevision.addStep")}
            </Button>
          </div>
          {routing.map((step, idx) => (
            <div key={idx} className="mb-2 rounded border border-line p-2">
              <div className="grid gap-2 sm:grid-cols-2">
                <SelectInput
                  label={t("common.fields.workCenter")}
                  options={[
                    { value: "", label: t("catalog.productRevision.none") },
                    ...workCenters.map((w) => ({ value: w.id, label: w.name })),
                  ]}
                  value={step.workCenterId}
                  onChange={(e) =>
                    setRouting((r) =>
                      r.map((s, i) =>
                        i === idx ? { ...s, workCenterId: e.target.value } : s
                      )
                    )
                  }
                />
                <TextInput
                  label={t("catalog.productRevision.stepDescription")}
                  value={step.description}
                  onChange={(e) =>
                    setRouting((r) =>
                      r.map((s, i) =>
                        i === idx ? { ...s, description: e.target.value } : s
                      )
                    )
                  }
                />
                <TextInput
                  label={t("catalog.productRevision.setupMin")}
                  value={step.setupMinutes}
                  onChange={(e) =>
                    setRouting((r) =>
                      r.map((s, i) =>
                        i === idx ? { ...s, setupMinutes: e.target.value } : s
                      )
                    )
                  }
                />
                <TextInput
                  label={t("catalog.productRevision.runMinPerUnit")}
                  value={step.runMinutesPerUnit}
                  onChange={(e) =>
                    setRouting((r) =>
                      r.map((s, i) =>
                        i === idx ? { ...s, runMinutesPerUnit: e.target.value } : s
                      )
                    )
                  }
                />
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRouting((r) => r.filter((_, i) => i !== idx))}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t("common.remove")}
              </Button>
            </div>
          ))}
        </section>

        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">
              {t("catalog.productRevision.bom")}
            </h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setBom((b) => [
                  ...b,
                  { materialId: "", quantityPer: "1", scrapFactor: "0" },
                ])
              }
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("catalog.productRevision.addMaterial")}
            </Button>
          </div>
          {bom.map((line, idx) => (
            <div
              key={idx}
              className="mb-2 grid items-end gap-2 rounded border border-line p-2 sm:grid-cols-3"
            >
              <SelectInput
                label={t("common.fields.material")}
                options={materials.map((m) => ({
                  value: m.id,
                  label: `${m.sku} · ${m.name}`,
                }))}
                value={line.materialId}
                onChange={(e) =>
                  setBom((b) =>
                    b.map((l, i) =>
                      i === idx ? { ...l, materialId: e.target.value } : l
                    )
                  )
                }
              />
              <TextInput
                label={t("catalog.productRevision.qtyPerUnit")}
                value={line.quantityPer}
                onChange={(e) =>
                  setBom((b) =>
                    b.map((l, i) =>
                      i === idx ? { ...l, quantityPer: e.target.value } : l
                    )
                  )
                }
              />
              <div className="flex items-end gap-2">
                <TextInput
                  label={t("catalog.productRevision.scrapFactor")}
                  value={line.scrapFactor}
                  onChange={(e) =>
                    setBom((b) =>
                      b.map((l, i) =>
                        i === idx ? { ...l, scrapFactor: e.target.value } : l
                      )
                    )
                  }
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBom((b) => b.filter((_, i) => i !== idx))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </section>
      </div>
    </Modal>
  );
}
